import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, Clock, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATTERN_STYLES = {
  early: { label: '早', bg: 'bg-blue-100 text-blue-800 border-blue-200' },
  day: { label: '日', bg: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  late: { label: '遅', bg: 'bg-amber-100 text-amber-800 border-amber-200' },
  night: { label: '夜', bg: 'bg-violet-100 text-violet-800 border-violet-200' },
  off: { label: '休', bg: 'bg-gray-100 text-gray-500 border-gray-200' },
};

export default function ShiftsPage() {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [staffList, setStaffList] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);

  useEffect(() => { fetchData(); }, [currentMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, shiftsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/staff`),
        axios.get(`${API}/shifts`, { params: { month: currentMonth } }),
        axios.get(`${API}/shifts/summary`, { params: { month: currentMonth } })
      ]);
      setStaffList(staffRes.data.filter(s => s.is_active !== false));
      setShifts(shiftsRes.data);
      setSummary(summaryRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const daysInMonth = useMemo(() => {
    const [y, m] = currentMonth.split('-').map(Number);
    const count = new Date(y, m, 0).getDate();
    return Array.from({ length: count }, (_, i) => {
      const d = i + 1;
      const dateStr = `${currentMonth}-${String(d).padStart(2, '0')}`;
      const dow = new Date(y, m - 1, d).getDay();
      return { day: d, dateStr, dow, isWeekend: dow === 0 || dow === 6 };
    });
  }, [currentMonth]);

  const DOW_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

  const shiftMap = useMemo(() => {
    const map = {};
    shifts.forEach(s => { map[`${s.staff_id}_${s.date}`] = s; });
    return map;
  }, [shifts]);

  const summaryMap = useMemo(() => {
    const map = {};
    summary.forEach(s => { map[s.staff_id] = s; });
    return map;
  }, [summary]);

  const changeMonth = (delta) => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleCellClick = (staffId, dateStr) => {
    setEditingCell({ staffId, dateStr });
  };

  const handlePatternSelect = async (pattern) => {
    if (!editingCell) return;
    try {
      await axios.post(`${API}/shifts`, {
        staff_id: editingCell.staffId,
        date: editingCell.dateStr,
        pattern: pattern
      });
      setEditingCell(null);
      fetchData();
    } catch (e) {
      alert('シフト登録に失敗: ' + (e.response?.data?.detail || e.message));
    }
  };

  const [y, m] = currentMonth.split('-').map(Number);
  const monthLabel = `${y}年${m}月`;

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="shifts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">シフト管理</h1>
          <p className="text-muted-foreground mt-1">月間シフト表</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="prev-month"><ChevronLeft size={18} /></Button>
          <span className="font-semibold text-lg min-w-[120px] text-center" data-testid="current-month">{monthLabel}</span>
          <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="next-month"><ChevronRight size={18} /></Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(PATTERN_STYLES).map(([key, val]) => (
          <span key={key} className={`text-xs px-2.5 py-1 rounded border ${val.bg}`}>{val.label} = {key === 'early' ? '早番 07-16' : key === 'day' ? '日勤 09-18' : key === 'late' ? '遅番 12-21' : key === 'night' ? '夜勤 21-07' : '休み'}</span>
        ))}
      </div>

      {staffList.length === 0 ? (
        <Card className="card-surface"><CardContent className="py-12 text-center">
          <Calendar size={48} className="mx-auto mb-4 text-muted-foreground/50" />
          <p className="text-muted-foreground">先に職員を登録してください</p>
        </CardContent></Card>
      ) : (
        <Card className="card-surface">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="shift-table">
                <thead>
                  <tr className="border-b">
                    <th className="sticky left-0 z-10 bg-card px-3 py-2 text-left font-medium min-w-[120px]">職員</th>
                    {daysInMonth.map(d => (
                      <th key={d.day} className={`px-1 py-2 text-center font-medium min-w-[36px] ${d.isWeekend ? (d.dow === 0 ? 'text-red-500 bg-red-50/50' : 'text-blue-500 bg-blue-50/50') : ''}`}>
                        <div className="text-xs">{d.day}</div>
                        <div className="text-[10px] text-muted-foreground">{DOW_LABELS[d.dow]}</div>
                      </th>
                    ))}
                    <th className="px-3 py-2 text-center font-medium min-w-[60px]">出勤</th>
                    <th className="px-3 py-2 text-center font-medium min-w-[60px]">時間</th>
                  </tr>
                </thead>
                <tbody>
                  {staffList.map(staff => {
                    const sm = summaryMap[staff.id] || { total_days: 0, total_hours: 0 };
                    return (
                      <tr key={staff.id} className="border-b hover:bg-secondary/30" data-testid={`shift-row-${staff.id}`}>
                        <td className="sticky left-0 z-10 bg-card px-3 py-1.5 font-medium text-xs whitespace-nowrap">
                          {staff.name}
                          <div className="text-[10px] text-muted-foreground">{staff.position}</div>
                        </td>
                        {daysInMonth.map(d => {
                          const shift = shiftMap[`${staff.id}_${d.dateStr}`];
                          const isEditing = editingCell?.staffId === staff.id && editingCell?.dateStr === d.dateStr;
                          const style = shift ? PATTERN_STYLES[shift.pattern] : null;
                          return (
                            <td key={d.day} className={`px-0.5 py-1 text-center relative ${d.isWeekend ? (d.dow === 0 ? 'bg-red-50/30' : 'bg-blue-50/30') : ''}`}>
                              {isEditing ? (
                                <div className="absolute z-20 top-0 left-1/2 -translate-x-1/2 bg-card shadow-lg rounded-lg border p-1 flex flex-col gap-0.5 min-w-[40px]">
                                  {Object.entries(PATTERN_STYLES).map(([key, val]) => (
                                    <button key={key} onClick={() => handlePatternSelect(key)}
                                      className={`text-[10px] px-1.5 py-0.5 rounded border ${val.bg} hover:opacity-80`}
                                      data-testid={`select-pattern-${key}`}>{val.label}</button>
                                  ))}
                                  <button onClick={() => setEditingCell(null)} className="text-[10px] text-muted-foreground hover:text-foreground">✕</button>
                                </div>
                              ) : null}
                              <button
                                onClick={() => handleCellClick(staff.id, d.dateStr)}
                                className={`w-7 h-7 rounded text-[11px] font-medium border transition-colors ${style ? style.bg : 'border-transparent hover:bg-secondary'}`}
                                data-testid={`cell-${staff.id}-${d.day}`}
                              >
                                {style ? style.label : ''}
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-3 py-1.5 text-center text-xs font-medium">{sm.total_days}日</td>
                        <td className="px-3 py-1.5 text-center text-xs font-medium">{sm.total_hours.toFixed(1)}h</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
